import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import config from "./config";
import { uploadTimeoutFor } from "./uploadMedia";
import applyCommunityFont, { applyCommunityDirection, applyCommunityTheme } from "./applyFont";
import { applyThemeMode, paintedMode, resolveThemeMode } from "./theme";
import { paletteForMode } from "./brandColor";
import PageLoader from "./PageLoader";
import { setFavicon, DEFAULT_FAVICON } from "./favicon";
import { brandBackgroundStyle } from "./lib/brandBackground";
import { DEFAULT_BRAND, FloatingField, ICON } from "./MembershipLogin";

/**
 * Public course review form — `/membership/:token/cr/:code`.
 *
 * The one page in this app a stranger is meant to open: the creator shares the
 * link outside the platform and customers leave a review without an account. So
 * there is no session here, and nothing on screen comes from one — the branding
 * is the membership's (api/getmembership, exactly as the login screen resolves
 * it) and the copy is the creator's, read from the course.
 *
 * Everything the visitor types is validated again on the server, which also
 * decides whether the form is open at all. Submissions land as pending: nothing
 * typed here is published anywhere until the creator approves it.
 */

const DEFAULT_HEADLINE = "Share your experience";
const DEFAULT_SUBHEADING = "Tell us what this course did for you — your review helps the next learner decide.";
const REVIEW_MAX = 2000;
// Matching the admin's course card: a 2x2 grid for modules, a graduation cap for
// lessons.
const ICON_MODULES = "M4.5 5.75A1.25 1.25 0 0 1 5.75 4.5h3A1.25 1.25 0 0 1 10 5.75v3A1.25 1.25 0 0 1 8.75 10h-3A1.25 1.25 0 0 1 4.5 8.75v-3Zm9.5 0a1.25 1.25 0 0 1 1.25-1.25h3a1.25 1.25 0 0 1 1.25 1.25v3A1.25 1.25 0 0 1 18.25 10h-3A1.25 1.25 0 0 1 14 8.75v-3Zm-9.5 9.5A1.25 1.25 0 0 1 5.75 14h3A1.25 1.25 0 0 1 10 15.25v3a1.25 1.25 0 0 1-1.25 1.25h-3a1.25 1.25 0 0 1-1.25-1.25v-3Zm9.5 0A1.25 1.25 0 0 1 15.25 14h3a1.25 1.25 0 0 1 1.25 1.25v3a1.25 1.25 0 0 1-1.25 1.25h-3A1.25 1.25 0 0 1 14 18.25v-3Z";
const ICON_LESSONS = "M12 6.25 3.5 9.5 12 12.75 20.5 9.5 12 6.25Z M6.5 11.5v4c0 1.1 2.46 2.5 5.5 2.5s5.5-1.4 5.5-2.5v-4";
const PHOTO_MAX_BYTES = 2 * 1024 * 1024;

/** The star picker. Read-only once the review is sent. */
function StarPicker({ value, onChange, theme, disabled }) {
    const [hover, setHover] = useState(0);
    const shown = hover || value;

    return (
        <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Star rating">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={value === n}
                    aria-label={`${n} star${n === 1 ? "" : "s"}`}
                    disabled={disabled}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => onChange(n)}
                    className="rounded-md p-0.5 transition-transform hover:scale-110 disabled:cursor-default disabled:hover:scale-100"
                >
                    <svg
                        className="h-8 w-8 transition-colors"
                        viewBox="0 0 24 24"
                        fill={n <= shown ? "currentColor" : "none"}
                        stroke="currentColor"
                        strokeWidth="1.5"
                        style={{ color: n <= shown ? theme : "rgb(var(--c-gray-300))" }}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11.48 3.5a.56.56 0 0 1 1.04 0l2.13 5.11c.08.2.27.34.49.36l5.51.44c.52.04.73.7.34 1.04l-4.2 3.6a.56.56 0 0 0-.19.57l1.28 5.37c.12.51-.43.91-.88.64l-4.72-2.88a.56.56 0 0 0-.58 0l-4.72 2.88c-.45.27-1-.13-.88-.64l1.28-5.37a.56.56 0 0 0-.19-.57l-4.2-3.6c-.39-.34-.18-1 .34-1.04l5.51-.44a.56.56 0 0 0 .49-.36l2.13-5.11Z"
                        />
                    </svg>
                </button>
            ))}
        </div>
    );
}

export default function CourseReviewForm() {
    const { token, code } = useParams();

    const [funnel, setFunnel] = useState(null);
    const [formMeta, setFormMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fatal, setFatal] = useState("");

    const [values, setValues] = useState({ name: "", email: "", review: "" });
    const [rating, setRating] = useState(0);
    /*
     * The chosen photo, checked immediately but uploaded only when the review is
     * sent.
     *
     * Everything that actually goes wrong with a profile photo — too big, wrong
     * format — is knowable in the browser, so it is said the moment the file is
     * picked, with no network call at all. Uploading at pick time would instead
     * put a file on the media service for every swap and for every visitor who
     * changes their mind and leaves, and nothing ever deletes those.
     */
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [photoBusy, setPhotoBusy] = useState(false);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    // The review box paints its own focus state, like FloatingField does. Without
    // it the browser's default blue ring shows up on the one field that isn't a
    // FloatingField — the only thing on the page not wearing the creator's colour.
    const [reviewFocus, setReviewFocus] = useState(false);
    const [sent, setSent] = useState(false);
    // A second review from an email that already reviewed this course. Its own
    // state, not an error banner: the person did nothing wrong, and the honest
    // answer is "we already have yours".
    const [already, setAlready] = useState(false);
    const fileRef = useRef(null);

    const brand = useMemo(() => ({ ...DEFAULT_BRAND, ...(funnel?.brand || {}) }), [funnel]);
    const palette = paletteForMode(brand, paintedMode());
    const theme = palette.primary;
    const btnTxt = palette.btnText;

    const didInit = useRef(false);
    useEffect(() => {
        if (didInit.current) return;
        didInit.current = true;
        (async () => {
            try {
                // Branding first, so even "form not found" wears the creator's
                // colours rather than a bare white page.
                const res = await axios.post(`${config.API_URL}/getmembership`, {
                    membership_code: token,
                    path: "login",
                    base_url: window.location.host,
                });
                const fd = res.data?.funnel_data?.[0];
                if (!res.data?.success || !fd) {
                    setFatal(res.data?.message || "Membership not found.");
                    return;
                }
                let parsed = {};
                try {
                    parsed = fd.brand_settings
                        ? typeof fd.brand_settings === "string"
                            ? JSON.parse(fd.brand_settings)
                            : fd.brand_settings
                        : {};
                } catch {
                    parsed = {};
                }
                setFunnel({ funnel_id: fd.funnel_id, name: fd.name, brand: parsed });
                if (parsed.membership_font) applyCommunityFont(parsed.membership_font);
                applyCommunityDirection(parsed?.rtl_enabled);
                applyCommunityTheme(parsed);
                applyThemeMode(resolveThemeMode(parsed, token), token);
                setFavicon(fd.favicon_url || DEFAULT_FAVICON);

                const meta = await axios.post(`${config.COMMUNITY_API_URL}/membership-course-review-form`, {
                    community_token: token,
                    course_code: code,
                    base_url: window.location.host,
                });
                if (meta.data?.error === 0) {
                    setFormMeta(meta.data);
                    document.title = `${meta.data.course?.name || "Course"} · Review`;
                } else {
                    setFatal(meta.data?.msg || "This review form could not be found.");
                }
            } catch (err) {
                setFatal(
                    err?.response?.status === 404
                        ? "This review form could not be found."
                        : "Something went wrong. Please try again.",
                );
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const set = (k) => (v) => {
        setValues((s) => ({ ...s, [k]: v }));
        setErrors((e) => (e[k] ? { ...e, [k]: "" } : e));
    };

    /**
     * Check the photo the moment it is chosen — size and format are both
     * knowable here — and keep it for the upload that happens on Send.
     */
    const pickPhoto = (file) => {
        if (!file) return;
        if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
            setErrors((e) => ({ ...e, photo: "Please use a JPG, PNG or WEBP photo." }));
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        if (file.size > PHOTO_MAX_BYTES) {
            setErrors((e) => ({ ...e, photo: "That photo is over 2 MB — please use a smaller one." }));
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        setErrors((e) => ({ ...e, photo: "" }));
        setPhoto(file);
        setPhotoPreview(URL.createObjectURL(file));
    };

    const clearPhoto = () => {
        setPhoto(null);
        setPhotoPreview("");
        setErrors((e) => ({ ...e, photo: "" }));
        if (fileRef.current) fileRef.current.value = "";
    };

    const submit = async (e) => {
        e.preventDefault();
        // Checked here so the visitor is told which field is wrong, next to the
        // field. The server checks all of it again regardless.
        const next = {};
        if (!values.name.trim()) next.name = "Please tell us your name.";
        if (!values.review.trim()) next.review = "Please write your review.";
        // Email and the rating are optional — only checked when given.
        if (values.email.trim() && !/^\S+@\S+\.\S+$/.test(values.email.trim())) {
            next.email = "That email address does not look right.";
        }
        setErrors(next);
        if (Object.keys(next).length) return;

        setBusy(true);
        try {
            /*
             * The photo goes to the shared media service first — the same one the
             * community composer uses for post images — and the review then
             * carries only the URL it answered with.
             *
             * That service insists on a `community_member_id` and a reviewer has
             * none; it only uses the value to name the file, so the COURSE id goes
             * in its place.
             *
             * A failure here stops the send rather than dropping the photo
             * silently: the review is still in the form, so the visitor can try
             * again or remove the photo and send without it — which is their
             * decision to make, not ours.
             */
            let photoUrl = "";
            if (photo) {
                setPhotoBusy(true);
                try {
                    const up = new FormData();
                    up.append("community_token", token);
                    up.append("community_member_id", String(formMeta?.course?.id || 0));
                    up.append("images[]", photo);
                    const upRes = await axios.post(`${config.MEDIA_API_URL}/upload`, up, {
                        headers: { "Content-Type": "multipart/form-data" },
                        // The global 25s axios timeout is for JSON calls; a photo
                        // on a slow upstream needs a budget sized to the file.
                        timeout: uploadTimeoutFor(photo?.size || 0),
                    });
                    photoUrl = upRes.data?.url || upRes.data?.urls?.[0] || "";
                    if (!photoUrl) throw new Error(upRes.data?.msg || "upload failed");
                } catch (err) {
                    setErrors({
                        photo: err?.response?.data?.msg || "That photo could not be uploaded. Try another one, or remove it and send without a photo.",
                    });
                    return;
                } finally {
                    setPhotoBusy(false);
                }
            }

            const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-course-review-submit`, {
                community_token: token,
                course_code: code,
                base_url: window.location.host,
                name: values.name.trim(),
                email: values.email.trim(),
                review: values.review.trim(),
                // Omitted entirely when nothing was picked. Sending 0 fails the
                // API's min:1 rule — "no rating" is the absence of the field,
                // not a zero-star rating — and the row stores 0 for it.
                ...(rating ? { rating } : {}),
                photo_url: photoUrl,
            });
            if (res.data?.error === 0) {
                setSent(true);
                return;
            }
            if (res.data?.already) {
                setAlready(true);
                return;
            }
            setErrors({ form: res.data?.msg || "Could not send your review." });
        } catch (err) {
            if (err?.response?.data?.already) {
                setAlready(true);
                return;
            }
            setErrors({ form: err?.response?.data?.msg || "Could not send your review. Please try again." });
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#f3f3f3] dark:bg-canvas">
                <PageLoader primary={theme} logo={brand.logo} />
            </div>
        );
    }

    /*
     * The page's backdrop.
     *
     * The brand background is authored for the membership's OWN mode — this one
     * is a near-black #0f0f12 — so painting it behind light-mode cards gives two
     * big white panels floating on black, which is what a viewer who flips to
     * light mode was trying to get away from. In light mode the app's own canvas
     * token is used instead, with a breath of the brand colour so the page is
     * still theirs; dark mode keeps the background exactly as the creator set it.
     */
    const lightMode = paintedMode() === "light";
    const bgStyle = lightMode
        ? { background: `radial-gradient(1200px 600px at 50% -10%, ${theme}1f, transparent 70%), rgb(var(--c-canvas))` }
        : brandBackgroundStyle(brand);
    const course = formMeta?.course || {};

    // Two columns only when there is a course card to put in the second one.
    const showCourse = !!formMeta?.show_course && !!course.name;
    const shell = (children, wide = false) => (
        <div className="min-h-screen w-full flex items-center justify-center p-4 relative" style={bgStyle}>
            {!lightMode && brand.bgmode !== "color" && Number(brand.enable_overlay) === 1 && (
                <div className="absolute inset-0" style={{ background: brand.overlaycolor || "rgba(0,0,0,0.5)" }} aria-hidden="true" />
            )}
            <div dir="ltr" className={`relative w-full ${wide ? "max-w-[980px]" : "max-w-[560px]"}`}>
                {children}
            </div>
        </div>
    );

    if (fatal) {
        return shell(
            <div className="rounded-2xl bg-surface px-7 py-10 text-center shadow-2xl">
                <img src={brand.logo} alt="" className="mx-auto h-[52px] w-auto object-contain" />
                <p className="mt-5 text-lg font-bold text-gray-900">{fatal}</p>
                <p className="mt-1.5 text-sm text-gray-500">Check the link with whoever sent it to you.</p>
            </div>,
        );
    }

    if (already) {
        return shell(
            <div className="rounded-2xl bg-surface px-7 py-12 text-center shadow-xl ring-1 ring-gray-900/[0.06]">
                <span
                    className="mx-auto grid h-16 w-16 place-items-center rounded-full"
                    style={{ backgroundColor: `${theme}1f`, color: theme }}
                >
                    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                </span>
                <h1 className="mt-5 text-[22px] font-bold text-gray-900">You have already reviewed this</h1>
                <p className="mx-auto mt-2 max-w-sm text-[14.5px] leading-relaxed text-gray-500">
                    There is already a review from <span className="font-semibold text-gray-700">{values.email.trim()}</span>
                    {course.name ? <> for <span className="font-semibold text-gray-700">{course.name}</span></> : null}. Thanks again —
                    one review per person is plenty.
                </p>
            </div>,
        );
    }

    if (sent) {
        return shell(
            <div className="rounded-2xl bg-surface px-7 py-12 text-center shadow-2xl">
                <span
                    className="mx-auto grid h-16 w-16 place-items-center rounded-full"
                    style={{ backgroundColor: `${theme}1f`, color: theme }}
                >
                    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4.5 4.5L19 7" />
                    </svg>
                </span>
                <h1 className="mt-5 text-[22px] font-bold text-gray-900">Thank you!</h1>
                {/* Deliberately says nothing about approval or publishing: reviews
                    go live as they arrive, and whether they show inside the course
                    is the creator's setting, not something to promise a stranger. */}
                <p className="mx-auto mt-2 max-w-sm text-[14.5px] leading-relaxed text-gray-500">
                    Your review{course.name ? <> of <span className="font-semibold text-gray-700">{course.name}</span></> : null} has been
                    sent. Thanks for taking the time to share it.
                </p>
                <div className="mt-6 flex justify-center">
                    <StarPicker value={rating} onChange={() => {}} theme={theme} disabled />
                </div>
            </div>,
        );
    }

    // Each line is shown only when the creator switched it on: the API sends null
    // for a hidden one, and an empty-but-shown line falls back to the default copy
    // — the same text the admin field is pre-filled with.
    const headline = formMeta?.headline === null ? "" : formMeta?.headline?.trim() || DEFAULT_HEADLINE;
    const subheading = formMeta?.subheading === null ? "" : formMeta?.subheading?.trim() || DEFAULT_SUBHEADING;

    return shell(
        <div className={`grid gap-5 ${showCourse ? "lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start" : ""}`}>
            <div className="overflow-hidden rounded-2xl bg-surface shadow-xl ring-1 ring-gray-900/[0.06]">
            {/* Header — logo and the creator's two lines. */}
            <div
                className="relative border-b border-gray-100 px-7 pb-6 pt-8 text-center"
                style={{ background: `linear-gradient(180deg, ${theme}${lightMode ? "14" : "1f"}, transparent)` }}
            >
                <img src={brand.logo} alt={funnel?.name || ""} className="mx-auto h-[46px] w-auto object-contain" />
                {/* Whose membership this is. The logo may be a wordmark, an icon,
                    or the platform default — none of which necessarily names the
                    place, and a link arriving cold in a WhatsApp group should. */}
                {funnel?.name ? (
                    <p className="mt-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-gray-400">{funnel.name}</p>
                ) : null}
                {headline ? (
                    <h1 className="mt-4 text-[22px] font-bold leading-snug text-gray-900">{headline}</h1>
                ) : null}
                {subheading ? (
                    <p className="mx-auto mt-1.5 max-w-md text-[14px] leading-relaxed text-gray-500">{subheading}</p>
                ) : null}
            </div>

            <form onSubmit={submit} className="space-y-4 border-t border-gray-100 px-7 py-7">
                    {errors.form ? (
                        <div className="rounded-md border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-500/30 dark:bg-red-500/15 dark:text-red-300">
                            {errors.form}
                        </div>
                    ) : null}

                    {/* The rating comes first: it is the one field everyone fills,
                        and asking for it up front sets the tone for the rest. */}
                    <div className="rounded-2xl border border-gray-200 bg-gray-50/60 px-4 py-4 text-center dark:bg-surface">
                        <p className="text-[13px] font-semibold text-gray-700">
                            How would you rate this course? <span className="font-medium text-gray-400">(optional)</span>
                        </p>
                        <div className="mt-2 flex justify-center">
                            <StarPicker value={rating} onChange={(n) => setRating(n)} theme={theme} />
                        </div>
                    </div>

                    <FloatingField label="Your Name *" value={values.name} onChange={set("name")} theme={theme} disabled={busy} error={errors.name} />
                    <FloatingField label="Your Email (optional)" type="email" value={values.email} onChange={set("email")} theme={theme} disabled={busy} error={errors.email} icon={ICON.mail} />

                    <div>
                        <label className="mb-1.5 block text-[13px] font-semibold text-gray-700">
                            Review Description <span className="font-bold text-red-500">*</span>
                        </label>
                        <textarea
                            value={values.review}
                            maxLength={REVIEW_MAX}
                            onChange={(e) => set("review")(e.target.value)}
                            disabled={busy}
                            rows={4}
                            placeholder="What did you learn? What changed for you?"
                            onFocus={() => setReviewFocus(true)}
                            onBlur={() => setReviewFocus(false)}
                            className="w-full resize-none rounded-md border bg-surface px-4 py-3 text-[15px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:outline-none"
                            style={{
                                borderColor: errors.review ? "#dc2626" : reviewFocus ? theme : "rgb(var(--c-gray-300))",
                                boxShadow: errors.review
                                    ? "0 0 0 2px rgba(220,38,38,0.15)"
                                    : reviewFocus
                                    ? `0 0 0 2px ${theme}22`
                                    : "none",
                            }}
                        />
                        <div className="mt-1 flex items-start justify-between gap-3">
                            {errors.review ? (
                                <p className="text-[12px] font-medium text-red-600 dark:text-red-300">{errors.review}</p>
                            ) : (
                                <span />
                            )}
                            <p className="shrink-0 text-[11.5px] tabular-nums text-gray-400">
                                {values.review.length}/{REVIEW_MAX}
                            </p>
                        </div>
                    </div>

                    {/* Photo — optional, and said so: an avatar requirement is the
                        kind of thing that quietly costs you reviews. */}
                    <div
                        className="flex items-center gap-3.5 rounded-2xl border border-dashed px-4 py-3.5 transition-colors"
                        style={{ borderColor: errors.photo ? "#dc2626" : "rgb(var(--c-gray-300))" }}
                    >
                        {photoPreview ? (
                            <span className="relative h-12 w-12 shrink-0">
                                <img src={photoPreview} alt="" className={`h-12 w-12 rounded-full object-cover ${photoBusy ? "opacity-50" : ""}`} />
                                {photoBusy ? (
                                    <span className="absolute inset-0 grid place-items-center">
                                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40" style={{ borderTopColor: theme }} />
                                    </span>
                                ) : null}
                            </span>
                        ) : (
                            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-gray-100 text-gray-400">
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.5 19.5a5.5 5.5 0 0 0-11 0M12 11.5a3.25 3.25 0 1 0 0-6.5 3.25 3.25 0 0 0 0 6.5Z" />
                                </svg>
                            </span>
                        )}
                        <div className="min-w-0 flex-1">
                            <p className="text-[13.5px] font-semibold text-gray-800">Profile photo</p>
                            {errors.photo ? (
                                <p className="mt-0.5 text-[12px] font-medium text-red-600 dark:text-red-300">{errors.photo}</p>
                            ) : photoBusy ? (
                                <p className="text-[12px] text-gray-400">Uploading…</p>
                            ) : photo ? (
                                <p className="text-[12px] font-medium" style={{ color: theme }}>
                                    {photo.name} · {(photo.size / 1024).toFixed(0)} KB
                                </p>
                            ) : (
                                <p className="text-[12px] text-gray-400">Optional · JPG, PNG or WEBP, up to 2 MB</p>
                            )}
                        </div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => pickPhoto(e.target.files?.[0])}
                        />
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => (photoPreview ? clearPhoto() : fileRef.current?.click())}
                            className="shrink-0 rounded-lg border border-gray-200 px-3 py-1.5 text-[13px] font-semibold transition-colors hover:bg-gray-50 disabled:opacity-50"
                            style={{ color: theme }}
                        >
                            {photoPreview ? "Remove" : "Upload"}
                        </button>
                    </div>

                    <button
                        type="submit"
                        disabled={busy || photoBusy}
                        className="w-full rounded py-3 text-center text-[16px] font-semibold transition hover:opacity-90 disabled:opacity-60"
                        style={{ backgroundColor: theme, color: btnTxt }}
                    >
                        {photoBusy ? "Uploading photo…" : busy ? "Sending…" : "Send review"}
                    </button>

                <p className="text-center text-[12px] text-gray-400">
                    Your email stays private - it is only shown to the course creator.
                </p>
            </form>
            </div>

            {/* The course being reviewed. Its own card beside the form, not a strip
                inside it: on a shared link the visitor may never have seen the
                course page, and this is what tells them what they are reviewing.
                The creator can switch it off in the admin popup. */}
            {showCourse ? (
                <aside className="rounded-2xl bg-surface p-4 shadow-xl ring-1 ring-gray-900/[0.06]">
                    <div className="aspect-[16/9] w-full overflow-hidden rounded-xl" style={{ backgroundColor: `${theme}14` }}>
                        {course.image ? (
                            <img src={course.image} alt="" className="h-full w-full object-cover" />
                        ) : (
                            <span className="grid h-full w-full place-items-center" style={{ color: theme }}>
                                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.25 3.5 9.5 12 12.75 20.5 9.5 12 6.25Z M6.5 11.5v4c0 1.1 2.46 2.5 5.5 2.5s5.5-1.4 5.5-2.5v-4" />
                                </svg>
                            </span>
                        )}
                    </div>
                    <p className="mt-3.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">Course</p>
                    <h2 className="mt-0.5 text-[15.5px] font-bold leading-snug text-gray-900">{course.name}</h2>
                    {course.description ? (
                        <p className="mt-2 text-[13px] leading-relaxed text-gray-500">{course.description}</p>
                    ) : null}
                    {course.modules || course.lessons ? (
                        <div className="mt-3.5 grid grid-cols-2 gap-2">
                            {[
                                ["MODULES", course.modules || 0, ICON_MODULES],
                                ["LESSONS", course.lessons || 0, ICON_LESSONS],
                            ].map(([label, value, path]) => (
                                <div key={label} className="flex items-center gap-2 rounded-xl border border-gray-200 px-2.5 py-2">
                                    <span
                                        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg"
                                        style={{ backgroundColor: `${theme}1f`, color: theme }}
                                    >
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                                            <path strokeLinecap="round" strokeLinejoin="round" d={path} />
                                        </svg>
                                    </span>
                                    <div className="leading-tight">
                                        <p className="text-[9px] font-bold uppercase tracking-wide text-gray-400">{label}</p>
                                        <p className="text-sm font-bold text-gray-900">{value}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </aside>
            ) : null}
        </div>,
        showCourse,
    );
}
